import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Filter, Clock, Users, Trash2, Loader2, Pencil, Plus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { HoursChangeRequestsCard } from './HoursChangeRequestsCard';
import { HoursEditorDialog } from './HoursEditorDialog';

export const TimeReportsTab = ({
  api,
  employees,
  projects,
  timeReportFilters,
  timeReportEmployees,
  timeReports,
  timeReportsLoading,
  onFilterChange,
  onApplyFilters,
  onDeleteEntry,
  onRefresh,
}) => {
  const [editor, setEditor] = useState({ open: false, initial: null });
  return (
    <div className="space-y-6">
      <HoursChangeRequestsCard api={api} onChanged={onRefresh} />
      <HoursEditorDialog api={api} open={editor.open} onOpenChange={(o) => setEditor(e => ({ ...e, open: o }))} employees={employees} projects={projects} initial={editor.initial} onSaved={onRefresh} />
      {/* Filters */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Start Date</Label>
              <Input
                type="date"
                value={timeReportFilters.start_date}
                onChange={(e) => onFilterChange('start_date', e.target.value)}
                className="bg-[#1E2530] border-white/20 text-white w-[160px]"
                data-testid="time-report-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">End Date</Label>
              <Input
                type="date"
                value={timeReportFilters.end_date}
                onChange={(e) => onFilterChange('end_date', e.target.value)}
                className="bg-[#1E2530] border-white/20 text-white w-[160px]"
                data-testid="time-report-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Employee</Label>
              <Select 
                value={timeReportFilters.employee_id || 'all'} 
                onValueChange={(v) => onFilterChange('employee_id', v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-[180px] bg-[#1E2530] border-white/20 text-white" data-testid="time-report-employee-filter">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Employees</SelectItem>
                  {timeReportEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id} className="text-white focus:bg-[#F5A623]/20">
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={onApplyFilters}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="apply-time-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">Total Hours</p>
            <p className="text-2xl font-bold text-[#F5A623]">{(timeReports.total_hours || 0).toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">Total Entries</p>
            <p className="text-2xl font-bold text-white">{timeReports.total_entries || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">Employees</p>
            <p className="text-2xl font-bold text-white">{(timeReports.employee_summaries || []).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">Date Range</p>
            <p className="text-sm font-medium text-white">
              {timeReports.date_range ? 
                `${format(parseISO(timeReports.date_range.start), 'dd MMM')} - ${format(parseISO(timeReports.date_range.end), 'dd MMM')}` :
                '-'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Employee Summaries */}
      {(timeReports.employee_summaries || []).length > 0 && (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold text-white mb-4">By Employee</h3>
            <div className="space-y-2">
              {(timeReports.employee_summaries || []).map((emp) => (
                <div key={emp.user_id} className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#F5A623]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{emp.user_name}</p>
                      {emp.trade && <p className="text-xs text-gray-500">{emp.trade}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#F5A623] font-bold">{(emp.total_hours || 0).toFixed(1)}h</p>
                    <p className="text-xs text-gray-500">{emp.entry_count} entries</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Entries Table */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Time Entries</h3>
            <Button size="sm" onClick={() => setEditor({ open: true, initial: null })} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="add-hours-btn">
              <Plus className="w-4 h-4 mr-1" /> Add hours
            </Button>
          </div>
          
          {timeReportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
            </div>
          ) : (timeReports.entries || []).length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">No time entries found for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-xs text-gray-500 pb-3 font-medium">Employee</th>
                    <th className="text-left text-xs text-gray-500 pb-3 font-medium">Project</th>
                    <th className="text-left text-xs text-gray-500 pb-3 font-medium">Date</th>
                    <th className="text-left text-xs text-gray-500 pb-3 font-medium">Clock In</th>
                    <th className="text-left text-xs text-gray-500 pb-3 font-medium">Clock Out</th>
                    <th className="text-right text-xs text-gray-500 pb-3 pr-4 font-medium">Hours</th>
                    <th className="text-left text-xs text-gray-500 pb-3 pl-2 font-medium">Type</th>
                    <th className="text-center text-xs text-gray-500 pb-3 font-medium w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {(timeReports.entries || []).map((entry) => (
                    <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5 group">
                      <td className="py-3">
                        <p className="text-white font-medium">{entry.user_name}</p>
                      </td>
                      <td className="py-3">
                        <p className="text-[#F5A623] text-sm">{entry.project_ref || '-'}</p>
                      </td>
                      <td className="py-3">
                        <p className="text-gray-400 text-sm">{entry.clock_in ? format(parseISO(entry.clock_in), 'dd MMM yyyy') : '-'}</p>
                      </td>
                      <td className="py-3">
                        <p className="text-white text-sm">{entry.kind === 'hours' ? '—' : entry.clock_in ? format(parseISO(entry.clock_in), 'HH:mm') : '-'}</p>
                      </td>
                      <td className="py-3">
                        <p className="text-white text-sm">{entry.kind === 'hours' ? '—' : entry.clock_out ? format(parseISO(entry.clock_out), 'HH:mm') : '-'}</p>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <p className={`font-medium ${entry.total_hours ? 'text-green-400' : 'text-amber-400'}`} data-testid={`time-entry-hours-${entry.id}`}>
                          {entry.total_hours ? `${entry.total_hours.toFixed(2).replace(/\.?0+$/, '')}h` : 'Active'}
                        </p>
                      </td>
                      <td className="py-3">
                        {entry.kind === 'hours' ? (
                          <Badge className="bg-[#F5A623]/15 text-[#F5A623] border-[#F5A623]/30" title={entry.updated_by_name ? `Last set by ${entry.updated_by_name}` : undefined}>
                            {entry.source === 'admin' ? 'Hours · admin' : entry.source === 'change_request' ? 'Hours · approved' : 'Hours'}
                          </Badge>
                        ) : (
                          <Badge className={entry.is_manual ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
                            {entry.is_manual ? 'Manual' : 'Clock'}
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 text-center">
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {entry.kind === 'hours' && (
                            <Button variant="ghost" size="sm" onClick={() => setEditor({ open: true, initial: { id: entry.id, user_id: entry.user_id, project_id: entry.project_id, date: entry.date, hours: entry.total_hours } })}
                              className="h-6 w-6 p-0 text-gray-300 hover:text-white hover:bg-white/10" data-testid={`edit-hours-${entry.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteEntry(entry)}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-time-entry-${entry.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
